import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Link2, CheckCircle2, Wallet as WalletIcon, BarChart3, Copy } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REG_STATUSES = ["Registered", "Invited", "Accepted", "Shipped", "Installed", "Completed", "Paid"];
const REQ_STATUSES = ["New", "In Review", "Confirmed", "Live", "Completed", "Declined"];
const REFERRAL_STATUSES = ["Pending", "Valid", "Rejected"];

const cell = "px-3 py-2 text-xs align-top";
const head = "px-3 py-2 text-left text-[10px] font-mono-spec uppercase tracking-wider text-slate-500";

function StatusSelect({ id, value, options, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(id, e.target.value)}
      className="border border-slate-300 rounded-sm text-xs px-2 py-1 bg-white text-slate-900"
    >
      {options.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
}

export default function Admin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | authed | anon
  const [token, setToken] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payouts, setPayouts] = useState({ total_pending: 0, count: 0, payouts: [] });
  const [linkStats, setLinkStats] = useState(null);

  const loadPayouts = async () => {
    try {
      const res = await api.get("/admin/payouts");
      setPayouts(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        const tok = me.data.token;
        setToken(tok);
        sessionStorage.setItem("lidlink-token", tok);
        const [regs, reqs, pays, stats] = await Promise.all([
          api.get("/admin/registrations"),
          api.get("/admin/campaign-requests"),
          api.get("/admin/payouts"),
          api.get("/admin/campaign-link-stats"),
        ]);
        setRegistrations(regs.data);
        setRequests(reqs.data);
        setPayouts(pays.data);
        setLinkStats(stats.data);
        setState("authed");
      } catch {
        setState("anon");
        navigate("/login");
      }
    })();
  }, [navigate]);

  const updateRegStatus = async (id, status) => {
    await api.patch(`/admin/registrations/${id}`, { status });
    setRegistrations((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const verifyProof = async (id) => {
    try {
      await api.patch(`/admin/registrations/${id}/verify-proof`);
      setRegistrations((rs) =>
        rs.map((r) => (r.id === id ? { ...r, proof_verified: true, status: "Installed" } : r))
      );
      toast.success("Nachweis verifiziert — Status: Installed");
    } catch {
      toast.error("Verifizierung fehlgeschlagen");
    }
  };

  const copyProofLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/nachweis/${id}`);
    toast.success("Nachweis-Link kopiert");
  };

  const updateReqStatus = async (id, status) => {
    await api.patch(`/admin/campaign-requests/${id}`, { status });
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const updateReferralStatus = async (id, status) => {
    await api.patch(`/admin/registrations/${id}/referral-status`, { status });
    setRegistrations((rs) => rs.map((r) => (r.id === id ? { ...r, referral_status: status } : r)));
    toast.success("Empfehlungsstatus aktualisiert");
    // Referrer credit may change — reload silently
    try {
      const regs = await api.get("/admin/registrations");
      setRegistrations(regs.data);
    } catch { /* ignore */ }
  };

  const completeCampaign = async (id) => {
    try {
      const res = await api.patch(`/admin/registrations/${id}/complete-campaign`);
      setRegistrations((rs) => rs.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
      await loadPayouts();
      toast.success("Erste Kampagne abgeschlossen — Guthaben ausgezahlt");
    } catch {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const [resending, setResending] = useState(false);

  const resendAll = async () => {
    if (!window.confirm("Neue Bestätigungsmails an alle noch nicht verifizierten Teilnehmer senden?")) return;
    setResending(true);
    try {
      const res = await api.post("/admin/resend-verifications");
      toast.success(`Gesendet: ${res.data.sent} · Fehlgeschlagen: ${res.data.failed}`);
    } catch {
      toast.error("Senden fehlgeschlagen");
    } finally {
      setResending(false);
    }
  };

  const resendOne = async (id) => {
    try {
      await api.post(`/admin/registrations/${id}/resend-verification`);
      toast.success("Bestätigungsmail gesendet");
    } catch {
      toast.error("Senden fehlgeschlagen");
    }
  };

  const campaignLink = `${window.location.origin}/start`;
  const copyCampaignLink = () => {
    navigator.clipboard.writeText(campaignLink);
    toast.success("Kampagnen-Link kopiert");
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/login");
  };

  if (state !== "authed") {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center font-mono-spec text-sm" data-testid="admin-loading">
        {t.admin.loading}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="admin-dashboard">
      <div className="bg-slate-950 text-white px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Logo dark markClass="w-5 h-5 text-blue-500" textClass="text-lg" />
          <span className="text-xs font-mono-spec text-slate-400 font-normal border-l border-slate-700 pl-3">{t.admin.title}</span>
        </Link>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="text-xs font-mono-spec text-slate-400 hover:text-white transition-colors"
        >
          {t.admin.logout} →
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="registrations">
          <TabsList className="mb-6">
            <TabsTrigger value="registrations" data-testid="admin-tab-registrations">
              {t.admin.registrations} ({registrations.length})
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests">
              {t.admin.requests} ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts">
              Auszahlungen ({payouts.count})
            </TabsTrigger>
            <TabsTrigger value="campaign-link" data-testid="admin-tab-campaign-link">
              Kampagnen-Link
            </TabsTrigger>
          </TabsList>

          <TabsContent value="registrations">
            {registrations.some((r) => !r.email_verified) && (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="admin-resend-bar">
                <span className="text-xs font-mono-spec text-blue-900">
                  {registrations.filter((r) => !r.email_verified).length} Teilnehmer mit offener E-Mail-Bestätigung
                </span>
                <button
                  data-testid="admin-resend-all-btn"
                  onClick={resendAll}
                  disabled={resending}
                  className="px-4 py-2 bg-slate-950 hover:bg-blue-700 text-white text-xs font-mono-spec font-bold rounded-full transition-colors disabled:opacity-50"
                >
                  {resending ? "Senden …" : "Neue Bestätigungsmails senden"}
                </button>
              </div>
            )}
            <div className="bg-white border spec-border rounded-sm overflow-x-auto" data-testid="admin-registrations-table">
              {registrations.length === 0 ? (
                <p className="p-8 text-sm text-slate-500 font-mono-spec">{t.admin.empty}</p>
              ) : (
                <table className="w-full min-w-[1400px]">
                  <thead className="border-b spec-border bg-slate-50">
                    <tr>
                      <th className={head}>Name</th>
                      <th className={head}>E-Mail</th>
                      <th className={head}>Uni / Studiengang</th>
                      <th className={head}>Gerät</th>
                      <th className={head}>Nutzungsorte</th>
                      <th className={head}>Nachweis</th>
                      <th className={head}>Launch-Guthaben</th>
                      <th className={head}>{t.admin.status}</th>
                      <th className={head}>{t.admin.date}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((r) => (
                      <tr key={r.id} className="border-b spec-border last:border-0" data-testid={`admin-reg-row-${r.id.slice(0, 8)}`}>
                        <td className={cell + " font-medium"}>
                          <div className="flex items-center gap-1.5">
                            {r.name}
                            <button
                              data-testid={`admin-copy-proof-${r.id.slice(0, 8)}`}
                              onClick={() => copyProofLink(r.id)}
                              title="Nachweis-Link kopieren"
                              className="text-slate-400 hover:text-gold-600 transition-colors"
                            >
                              <Link2 size={13} />
                            </button>
                          </div>
                        </td>
                        <td className={cell}>{r.email}</td>
                        <td className={cell}>{r.university}<br /><span className="text-slate-500">{r.study_program} · {r.semester}</span></td>
                        <td className={cell}>{r.device_type}<br /><span className="text-slate-500">{r.device_model}</span></td>
                        <td className={cell}>{(r.usage_locations || []).join(", ")}</td>
                        <td className={cell} data-testid={`admin-proof-cell-${r.id.slice(0, 8)}`}>
                          {r.proof_path ? (
                            <div className="space-y-1.5">
                              <a
                                href={`${BACKEND}/api/files/${r.proof_path}?auth=${token}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={`${BACKEND}/api/files/${r.proof_path}?auth=${token}`}
                                  alt="Nachweis"
                                  data-testid={`admin-proof-img-${r.id.slice(0, 8)}`}
                                  className="w-20 h-14 object-cover border border-slate-200 rounded-sm hover:opacity-80 transition-opacity"
                                />
                              </a>
                              {r.proof_verified ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-mono-spec text-emerald-700" data-testid={`admin-proof-verified-${r.id.slice(0, 8)}`}>
                                  <CheckCircle2 size={11} /> Verifiziert
                                </span>
                              ) : (
                                <button
                                  data-testid={`admin-verify-btn-${r.id.slice(0, 8)}`}
                                  onClick={() => verifyProof(r.id)}
                                  className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-mono-spec font-bold rounded-sm transition-colors"
                                >
                                  ✓ Verifizieren
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className={cell} data-testid={`admin-credit-cell-${r.id.slice(0, 8)}`}>
                          <div className="space-y-1.5 min-w-[210px]">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.email_verified ? "bg-emerald-500" : "bg-slate-300"}`} />
                              <span className="text-[10px] text-slate-500">{r.email_verified ? "E-Mail bestätigt" : "E-Mail offen"}</span>
                              {!r.email_verified && (
                                <button
                                  data-testid={`admin-resend-one-${r.id.slice(0, 8)}`}
                                  onClick={() => resendOne(r.id)}
                                  className="text-[10px] font-mono-spec text-blue-700 underline hover:text-blue-900"
                                >
                                  erneut senden
                                </button>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-600">
                              Start: <b>{r.welcome_credit_status || "pending"}</b> · Bonus: <b>{r.referral_credit_status || "none"}</b>
                            </div>
                            <div className="text-[10px] text-slate-500">Gültige Empfehlungen: {r.valid_referrals_count ?? 0} / 3</div>
                            {r.referred_by && (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-400">Als Empfehlung:</span>
                                <StatusSelect id={r.id} value={r.referral_status || "Pending"} options={REFERRAL_STATUSES} onChange={updateReferralStatus} testId={`admin-referral-status-${r.id.slice(0, 8)}`} />
                              </div>
                            )}
                            {!r.first_campaign_completed ? (
                              <button
                                data-testid={`admin-complete-campaign-${r.id.slice(0, 8)}`}
                                onClick={() => completeCampaign(r.id)}
                                className="px-2 py-1 bg-slate-900 hover:bg-gold-800 text-white text-[10px] font-mono-spec font-bold rounded-sm transition-colors"
                              >
                                Kampagne abschließen &amp; auszahlen
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-mono-spec text-emerald-700">
                                <CheckCircle2 size={11} /> Ausgezahlt
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={cell}>
                          <StatusSelect id={r.id} value={r.status} options={REG_STATUSES} onChange={updateRegStatus} testId={`admin-reg-status-${r.id.slice(0, 8)}`} />
                        </td>
                        <td className={cell + " text-slate-500"}>{new Date(r.created_at).toLocaleDateString("de-DE")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="bg-white border spec-border rounded-sm overflow-x-auto" data-testid="admin-requests-table">
              {requests.length === 0 ? (
                <p className="p-8 text-sm text-slate-500 font-mono-spec">{t.admin.empty}</p>
              ) : (
                <table className="w-full min-w-[900px]">
                  <thead className="border-b spec-border bg-slate-50">
                    <tr>
                      <th className={head}>Unternehmen</th>
                      <th className={head}>Kontakt</th>
                      <th className={head}>Kontingent</th>
                      <th className={head}>Schätzung</th>
                      <th className={head}>Nachricht</th>
                      <th className={head}>{t.admin.status}</th>
                      <th className={head}>{t.admin.date}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b spec-border last:border-0" data-testid={`admin-req-row-${r.id.slice(0, 8)}`}>
                        <td className={cell + " font-medium"}>{r.company}</td>
                        <td className={cell}>{r.contact_person}<br /><span className="text-slate-500">{r.email}</span></td>
                        <td className={cell}>{r.quota || `${r.placements} · ${r.location} · ${r.duration_weeks}w`}</td>
                        <td className={cell}>{r.estimate_eur ? `€${Math.round(r.estimate_eur).toLocaleString("de-DE")}` : "—"}</td>
                        <td className={cell + " max-w-[240px]"}>{r.message}</td>
                        <td className={cell}>
                          <StatusSelect id={r.id} value={r.status} options={REQ_STATUSES} onChange={updateReqStatus} testId={`admin-req-status-${r.id.slice(0, 8)}`} />
                        </td>
                        <td className={cell + " text-slate-500"}>{new Date(r.created_at).toLocaleDateString("de-DE")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payouts">
            <div className="mb-4 flex items-center justify-between bg-slate-950 text-white px-5 py-4 rounded-sm" data-testid="admin-payouts-summary">
              <div className="flex items-center gap-2 text-xs font-mono-spec text-slate-300 uppercase tracking-wider">
                <WalletIcon size={15} /> Fällige Guthaben-Auszahlungen
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" data-testid="admin-payouts-total">€{payouts.total_pending.toFixed(2)}</div>
                <div className="text-[10px] font-mono-spec text-gold-400">{payouts.count} Teilnehmer · ausstehend</div>
              </div>
            </div>
            <p className="mb-4 text-xs font-mono-spec text-slate-500 leading-relaxed">
              Guthaben wird zusammen mit der ersten erfolgreich abgeschlossenen bezahlten Kampagne ausgezahlt. Mit „Abhaken" markierst du das Guthaben als ausgezahlt (Kampagne abgeschlossen).
            </p>
            <div className="bg-white border spec-border rounded-sm overflow-x-auto" data-testid="admin-payouts-table">
              {payouts.payouts.length === 0 ? (
                <p className="p-8 text-sm text-slate-500 font-mono-spec">Keine ausstehenden Auszahlungen.</p>
              ) : (
                <table className="w-full min-w-[820px]">
                  <thead className="border-b spec-border bg-slate-50">
                    <tr>
                      <th className={head}>Teilnehmer</th>
                      <th className={head}>Startguthaben</th>
                      <th className={head}>Empfehlungsbonus</th>
                      <th className={head}>Summe</th>
                      <th className={head}>Kampagne</th>
                      <th className={head}>Abhaken</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.payouts.map((p) => (
                      <tr key={p.id} className="border-b spec-border last:border-0" data-testid={`admin-payout-row-${p.id.slice(0, 8)}`}>
                        <td className={cell + " font-medium"}>
                          {p.name}<br /><span className="text-slate-500">{p.email}</span>
                        </td>
                        <td className={cell + " font-mono-spec"}>{p.welcome_pending > 0 ? `€${p.welcome_pending.toFixed(0)}` : "—"}</td>
                        <td className={cell + " font-mono-spec"}>{p.referral_pending > 0 ? `€${p.referral_pending.toFixed(0)}` : "—"}</td>
                        <td className={cell + " font-mono-spec font-bold"} data-testid={`admin-payout-total-${p.id.slice(0, 8)}`}>€{p.total_pending.toFixed(2)}</td>
                        <td className={cell}>{p.status}</td>
                        <td className={cell}>
                          <button
                            data-testid={`admin-payout-mark-${p.id.slice(0, 8)}`}
                            onClick={() => completeCampaign(p.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-gold-800 text-white text-[10px] font-mono-spec font-bold rounded-sm transition-colors"
                          >
                            <CheckCircle2 size={12} /> Ausgezahlt
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="campaign-link">
            <div className="mb-6 bg-slate-950 text-white rounded-lg p-5" data-testid="admin-campaign-link-box">
              <div className="flex items-center gap-2 text-xs font-mono-spec text-slate-300 uppercase tracking-wider mb-3">
                <BarChart3 size={15} /> Dein Ads-Link für Kampagnenanfragen
              </div>
              <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                Nutze diesen Link in deinen Anzeigen. Er führt Interessenten direkt zum Anfrageformular — jeder Klick und jeder Schritt wird hier gemessen.
              </p>
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-md p-2">
                <input
                  readOnly
                  value={campaignLink}
                  data-testid="admin-campaign-link-input"
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 bg-transparent text-xs font-mono-spec text-slate-200 px-2 py-1 outline-none truncate"
                />
                <button
                  data-testid="admin-campaign-link-copy"
                  onClick={copyCampaignLink}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-mono-spec font-bold rounded-md transition-colors"
                >
                  <Copy size={13} /> Kopieren
                </button>
              </div>
            </div>

            {linkStats && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="admin-campaign-stats">
                  {[
                    { label: "Besuche gesamt", value: linkStats.visits_total, testId: "stat-visits" },
                    { label: "Eindeutige Besucher", value: linkStats.unique_visitors, testId: "stat-unique" },
                    { label: "Formular begonnen", value: linkStats.form_started, testId: "stat-started" },
                    { label: "Anfrage abgeschickt", value: linkStats.submitted, testId: "stat-submitted" },
                    { label: "Conversion-Rate", value: `${linkStats.conversion_rate}%`, testId: "stat-conversion" },
                  ].map((s) => (
                    <div key={s.label} className="bg-white border spec-border rounded-lg p-4" data-testid={`admin-${s.testId}`}>
                      <div className="text-2xl font-bold text-slate-950">{s.value}</div>
                      <div className="text-[10px] font-mono-spec text-slate-500 uppercase tracking-wide mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="bg-white border spec-border rounded-lg p-5" data-testid="admin-campaign-funnel">
                    <h3 className="text-sm font-bold text-slate-950 mb-4">Funnel</h3>
                    {[
                      { label: "Besuche", value: linkStats.unique_visitors, pct: 100 },
                      { label: "Formular begonnen", value: linkStats.form_started, pct: linkStats.start_rate },
                      { label: "Anfrage abgeschickt", value: linkStats.submitted, pct: linkStats.conversion_rate },
                    ].map((f) => (
                      <div key={f.label} className="mb-3 last:mb-0">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-mono-spec text-slate-700">{f.label}</span>
                          <span className="font-mono-spec text-slate-500">{f.value} · {f.pct}%</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${Math.max(2, f.pct)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border spec-border rounded-lg p-5" data-testid="admin-campaign-daily">
                    <h3 className="text-sm font-bold text-slate-950 mb-4">Besuche — letzte 7 Tage</h3>
                    <div className="flex items-end justify-between gap-2 h-32">
                      {linkStats.daily.map((d) => {
                        const max = Math.max(1, ...linkStats.daily.map((x) => x.visits));
                        const h = Math.round((d.visits / max) * 100);
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
                            <div className="w-full flex items-end justify-center" style={{ height: "100px" }}>
                              <div
                                className="w-full bg-blue-500 rounded-t-sm min-h-[2px]"
                                style={{ height: `${h}%` }}
                                title={`${d.visits} Besuche`}
                              />
                            </div>
                            <span className="text-[9px] font-mono-spec text-slate-400">{d.date.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
